import { useState } from 'react'

import { useApp } from '../../../../app/AppContext.jsx'
import { useProduct } from '../../index.js'
import { useI18n } from '../../../../shared/i18n/useI18n.js'

import { Modal } from '../../../../shared/ui/Modal/index.js'
import { CollapsiblePane } from './CollapsiblePane.jsx'

import styles from './DetailsPane.module.scss'

const CLIP_AT = 160

/**
 * DetailsPane — product description panel.
 *
 * Content priority:
 *  1. Matched turnkey style description (text_html)
 *  2. Product description (text_html)
 *  3. null → pane returns null
 *
 * Truncation:
 *   If the plain text is longer than CLIP_AT characters, the body is clipped
 *   and a "Pokaż więcej" link opens a modal showing the full description,
 *   turnkey label (if applicable), and both texts as paragraphs.
 */
export function DetailsPane() {
  const { t } = useI18n()
  const { panelSettings } = useApp()
  const { selection } = useProduct()
  const { product } = selection

  const [isModalOpen, setIsModalOpen] = useState(false)

  const turnkeyStyles = panelSettings?.turn_key_styles?.styles ?? []
  const productTurnkeyLabel = product?.turn_key_style ?? null

  const matchedStyle = productTurnkeyLabel
    ? turnkeyStyles.find((s) => s.in_use && s.label === productTurnkeyLabel)
    : null

  const turnkeyDescHtml = matchedStyle?.description?.text_html ?? ''
  const turnkeyDescText = matchedStyle?.description?.text ?? ''
  const productDescHtml = product?.description?.text_html ?? ''
  const productDescText = product?.description?.text ?? ''

  // Priority: turnkey → product
  const primaryHtml = turnkeyDescHtml || productDescHtml
  const primaryText = turnkeyDescText || productDescText

  if (!primaryHtml && !primaryText) return null

  const isLong = primaryText.length > CLIP_AT
  const clippedText = isLong ? primaryText.slice(0, CLIP_AT).trimEnd() + '…' : null

  return (
    <>
      <CollapsiblePane
        title={t('product.pane_details_title')}
        collapseAriaLabel={t('product.pane_details_collapse_aria')}
        expandAriaLabel={t('product.pane_details_expand_aria')}
      >
        <div className={styles.detailsPane}>
          {isLong ? (
            <>
              <p className={styles.clippedText}>{clippedText}</p>
              <button
                className={styles.showMoreBtn}
                onClick={() => setIsModalOpen(true)}
              >
                {t('product.details_show_more')}
              </button>
            </>
          ) : (
            primaryHtml
              ? <div dangerouslySetInnerHTML={{ __html: primaryHtml }} />
              : <p>{primaryText}</p>
          )}
        </div>
      </CollapsiblePane>

      {isModalOpen && (
        <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
          <Modal.Header onClose={() => setIsModalOpen(false)} closeLabel={t('common.close')}>
            <span className={styles.heading}>{t('product.details_modal_title')}</span>
          </Modal.Header>
          <Modal.Content className={styles.modalContent}>
            {matchedStyle && (
              <p className={styles.modalTurnkeyLabel}>
                {t('product.turnkey_style')}: {matchedStyle.label}
              </p>
            )}
            {turnkeyDescHtml && (
              <div
                className={styles.modalSection}
                dangerouslySetInnerHTML={{ __html: turnkeyDescHtml }}
              />
            )}
            {productDescHtml && (
              <div
                className={styles.modalSection}
                dangerouslySetInnerHTML={{ __html: productDescHtml }}
              />
            )}
            {!turnkeyDescHtml && !productDescHtml && (
              <p>{primaryText}</p>
            )}
          </Modal.Content>
        </Modal>
      )}
    </>
  )
}
